/* eslint-disable @typescript-eslint/naming-convention */
import { defaultLayout, getRequestPayload, models, requestOverride } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import type { ContentItem } from '@ps/web-martech-cms-types';
import type { SportsContentItem } from '@ps/web-sports-page-types';
import { difference, get, pick } from 'lodash';
import { filter, take } from 'rxjs/operators';
import URL from 'url-parse';

import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetReference, WidgetType } from '@swf/core/types/Widget';
import { compareCacheControl } from '@swf/core/utils/compareCacheControl';
import { getLocalPage } from '@swf/core/utils/pages/pages';
import { fetchProperties } from '@swf/core/utils/widgets';

import type { CMSState, MenuResponse, PageResponse } from './index';

const cache: {
  pages: Record<string, PageResponse>;
  menus: Record<string, Omit<MenuResponse, 'content'>>;
  lastContent: Record<string, ContentItem | SportsContentItem>;
} = {
  pages: {},
  menus: {},
  lastContent: {}
};

interface CoreCmsEffects {
  fetchWebsite(payload: undefined, { request, dispatch, rootState }: EffectParams): Promise<void>;
}

// cast to make coreCmsEffects compatible with SW
const effects = {
  ...(models.cms.effects as unknown as CoreCmsEffects),
  async fetchPage({ href }: { href: string }, params: EffectParams) {
    const { rootState, dispatch, request } = params;
    const url = new URL(href, true);
    const payloadBody = getRequestPayload(href, rootState);
    const menuCachePath = payloadBody.path.startsWith('/sports/') ? '/sports/' : payloadBody.path;
    const menuCache = canUseDOM && cache.menus[menuCachePath];
    const pageCache = canUseDOM && cache.pages[payloadBody.path];
    let cacheControl: string | undefined;
    const pathBrand = select.cms.getPathBrand(rootState);

    if (payloadBody.siteId) {
      url.set('query', { ...url.query, siteId: payloadBody.siteId });
    }

    dispatch.cms.setPageLoaded(false);

    const localPage = await getLocalPage(rootState, payloadBody.path);

    const [page, menu] = await Promise.all([
      localPage ??
        (await (pageCache
          ? Promise.resolve(pageCache)
          : request<PageResponse>({
              service: 'cms.page',
              body: payloadBody,
              ...requestOverride
            }).then((response) => {
              if (canUseDOM) {
                cache.pages[payloadBody.path] = response.body;
              }

              if (response.headers.cacheControl) {
                cacheControl = compareCacheControl(response.headers.cacheControl, cacheControl);
              }
              return response.body;
            }))),
      menuCache
        ? Promise.resolve({ ...menuCache, content: {} })
        : request<MenuResponse>({
            service: 'cms.navigation',
            body: payloadBody,
            ...requestOverride
          })
            .then((response) => {
              const menuData = localPage ? { ...response.body, menu: undefined } : response.body;

              if (canUseDOM) {
                cache.menus[menuCachePath] = {
                  menu: menuData.menu,
                  depth: menuData.depth,
                  seo_menu: menuData.seo_menu,
                  seo_depth: menuData.seo_depth,
                  search: menuData.search,
                  menuView: menuData.menuView
                };
              }
              if (response.headers.cacheControl) {
                cacheControl = compareCacheControl(response.headers.cacheControl, cacheControl);
              }
              return menuData;
            })
            .catch(() => ({
              content: undefined,
              seo_menu: undefined
            }))
    ]);

    // TODO: CMS-migration - remove after stack content can be modified
    if (page?.page && !page.page.layout) page.page.layout = defaultLayout;
    // TODO: CMS-migration - end

    const content = { ...page?.content, ...menu?.content };

    const { lastContent } = cache;
    const diffContent = canUseDOM
      ? pick(content, difference(Object.keys(content), Object.keys(lastContent)))
      : content;
    cache.lastContent = content;

    await effects.prepare(diffContent, params);

    let payload: CMSState = {
      ...page,
      ...menu,
      seo_menu: menu.seo_menu || undefined,
      content,
      url,
      cacheControl
    };

    if (!payloadBody.path.includes('/cashier') || !pathBrand) {
      payload = { ...payload, pathBrand: select.cms.getPathBrandId(rootState, payloadBody.path) };
    }

    dispatch.cms.update(payload);

    await dispatch.cms.preload(diffContent);

    dispatch.cms.setPageLoaded(true);
  },
  async loadModel(
    { item, state }: { item: ContentItem | SportsContentItem; state?: unknown },
    { actions, addModel, dispatch }: EffectParams
  ) {
    const { uid, _content_type_uid } = item;
    const { model } = await fetchProperties(_content_type_uid as WidgetType);
    if (!model) return;
    const unmount = actions.pipe(
      filter(
        ({ payload, type }) => type === 'cms/update' && payload.url && !payload.content?.[uid]
      ),
      take(1)
    );
    const modelState = state ?? get(canUseDOM && window, ['__INITIAL_STATE__', uid], model.state);

    !dispatch[uid] &&
      addModel({
        ...model,
        state: modelState,
        name: uid,
        widget: { ...(item as WidgetReference), unmount }
      });
  },
  async prepare(data: CMSState['content'] | undefined, params: EffectParams) {
    await Promise.all(
      Object.values(data || select.cms.getContent(params.rootState)).map((widget) =>
        effects.loadModel({ item: widget }, params)
      )
    );
  },
  async preload(data: CMSState['content'] | undefined, { rootState, dispatch }: EffectParams) {
    await Promise.all(
      Object.values(data || select.cms.getContent(rootState)).map(({ uid }) => {
        if (canUseDOM && window.__INITIAL_STATE__) delete window.__INITIAL_STATE__[uid];
        const preload = dispatch[uid]?.preload?.().catch(() => undefined);
        return !canUseDOM ? preload : undefined;
      })
    );
  }
};

export default effects;
